import {
    DesignSystemBorderRadius,
    DesignSystemButtonRadius,
    DesignSystemButtonSize,
    DesignSystemButtonVariant,
    DesignSystemColorMode,
    DesignSystemColorScheme,
    DesignSystemIconSizing,
    DesignSystemTypographyVariants,
} from '@upheal/design-system/theme/design-system/types';

type VariantColorArgs = {
    variant: DesignSystemButtonVariant;
    colorScheme: DesignSystemColorScheme;
    colors: DesignSystemColorMode;
    disabled: boolean;
    underline: boolean;
};

type VariantColorOutput = {
    color: string;
    backgroundColor: string;
    borderColor: string;
    textDecoration?: string;
    '&:hover': {
        color?: string;
        backgroundColor: string;
        borderColor: string;
        textDecoration?: string;
    };
    '&:active': {
        color?: string;
        backgroundColor: string;
        borderColor: string;
        textDecoration?: string;
    };
    '&.Mui-disabled': {
        color: string;
        backgroundColor: string;
        borderColor: string;
        textDecoration?: string;
        '&:hover': {
            color?: string;
            backgroundColor: string;
            borderColor: string;
        };
    };
};

export function getVariantColorStyles({
    variant,
    colorScheme,
    colors,
    disabled,
    underline,
}: VariantColorArgs): VariantColorOutput {
    let enabledTextColor: string;
    let enabledBgColor: string;
    let enabledBorderColor: string;

    let hoverTextColor: string;
    let hoverBgColor: string;
    let hoverBorderColor: string;

    let activeTextColor: string;
    let activeBgColor: string;
    let activeBorderColor: string;

    let textDecoration: string | undefined = undefined;

    switch (variant) {
        case 'solid':
            if (colorScheme === 'neutral') {
                enabledTextColor = colors.fg.inverse.secondary;
                enabledBgColor = colors.bg.inverse.secondary;
                enabledBorderColor = 'transparent';

                hoverTextColor = colors.fg.inverse.primaryHover;
                hoverBgColor = colors.bg.inverse.primaryHover;
                hoverBorderColor = 'transparent';

                activeTextColor = colors.fg.inverse.primaryActive;
                activeBgColor = colors.bg.inverse.primaryActive;
                activeBorderColor = 'transparent';
            } else {
                enabledTextColor = colors.fg.onAccent.primary;
                enabledBgColor = colors.bg[colorScheme].primary;
                enabledBorderColor = 'transparent';

                hoverTextColor = colors.fg.onAccent.primaryHover;
                hoverBgColor = colors.bg[colorScheme].primaryHover;
                hoverBorderColor = 'transparent';

                activeTextColor = colors.fg.onAccent.primaryActive;
                activeBgColor = colors.bg[colorScheme].primaryActive;
                activeBorderColor = 'transparent';
            }

            break;
        case 'outline':
            if (colorScheme === 'neutral') {
                enabledTextColor = colors.fg[colorScheme].primary;
                enabledBgColor = 'transparent';
                enabledBorderColor = colors.border[colorScheme].primary;

                hoverTextColor = colors.fg[colorScheme].primaryHover;
                hoverBgColor = colors.bg[colorScheme].primaryHover;
                hoverBorderColor = colors.border[colorScheme].primaryHover;

                activeTextColor = colors.fg[colorScheme].primaryActive;
                activeBgColor = colors.bg[colorScheme].primaryActive;
                activeBorderColor = colors.border[colorScheme].primaryActive;
            } else {
                enabledTextColor = colors.fg[colorScheme].primary;
                enabledBgColor = 'transparent';
                enabledBorderColor = colors.border[colorScheme].primary;

                hoverTextColor = colors.fg[colorScheme].primaryHover;
                hoverBgColor = colors.bg[colorScheme].tertiaryHover;
                hoverBorderColor = colors.border[colorScheme].primaryHover;

                activeTextColor = colors.fg[colorScheme].primaryActive;
                activeBgColor = colors.bg[colorScheme].tertiaryActive;
                activeBorderColor = colors.border[colorScheme].primaryActive;
            }

            break;
        case 'ghost':
            if (colorScheme === 'neutral') {
                enabledTextColor = colors.fg[colorScheme].primary;
                enabledBgColor = 'transparent';
                enabledBorderColor = 'transparent';

                hoverTextColor = colors.fg[colorScheme].primaryHover;
                hoverBgColor = colors.bg[colorScheme].primaryHover;
                hoverBorderColor = 'transparent';

                activeTextColor = colors.fg[colorScheme].primaryActive;
                activeBgColor = colors.bg[colorScheme].primaryActive;
                activeBorderColor = 'transparent';
            } else {
                enabledTextColor = colors.fg[colorScheme].primary;
                enabledBgColor = 'transparent';
                enabledBorderColor = 'transparent';

                hoverTextColor = colors.fg[colorScheme].primaryHover;
                hoverBgColor = colors.bg[colorScheme].tertiaryHover;
                hoverBorderColor = 'transparent';

                activeTextColor = colors.fg[colorScheme].primaryActive;
                activeBgColor = colors.bg[colorScheme].tertiaryActive;
                activeBorderColor = 'transparent';
            }

            break;
        case 'link':
            textDecoration = underline ? 'underline !important' : undefined;
            enabledTextColor = colors.fg[colorScheme].primary;
            enabledBgColor = 'transparent';
            enabledBorderColor = 'transparent';

            hoverTextColor = colors.fg[colorScheme].primaryHover;
            hoverBgColor = 'transparent';
            hoverBorderColor = 'transparent';

            activeTextColor = colors.fg[colorScheme].primaryActive;
            activeBgColor = 'transparent';
            activeBorderColor = 'transparent';

            break;
        case 'buttonGroup':
            enabledTextColor = colors.fg.neutral.secondary;
            enabledBgColor = colors.bg.neutral.primary;
            enabledBorderColor = colors.border.neutral.primary;

            hoverTextColor = colors.fg.neutral.primaryHover;
            hoverBgColor = colors.bg.neutral.primary;
            hoverBorderColor = colors.border.neutral.primary;

            activeTextColor = colors.fg.neutral.primaryActive;
            activeBgColor = colors.bg.neutral.primaryActive;
            activeBorderColor = colors.border.neutral.primary;

            break;
        case 'buttonGroupProminent':
            enabledTextColor = colors.fg.accent1.primary;
            enabledBgColor = colors.bg.accent1.tertiary;
            enabledBorderColor = colors.border.neutral.primary;

            hoverTextColor = colors.fg.accent1.primaryHover;
            hoverBgColor = colors.bg.accent1.tertiaryHover;
            hoverBorderColor = colors.border.neutral.primary;

            activeTextColor = colors.fg.accent1.primaryActive;
            activeBgColor = colors.bg.accent1.tertiaryActive;
            activeBorderColor = colors.border.neutral.primary;

            break;
        case 'buttonGroupActive':
            enabledTextColor = colors.fg.neutral.primaryActive;
            enabledBgColor = colors.bg.neutral.tertiary;
            enabledBorderColor = colors.border.neutral.primary;

            hoverTextColor = colors.fg.neutral.primaryHover;
            hoverBgColor = colors.bg.neutral.secondaryHover;
            hoverBorderColor = colors.border.neutral.primary;

            activeTextColor = colors.fg.neutral.primaryActive;
            activeBgColor = colors.bg.neutral.secondaryActive;
            activeBorderColor = colors.border.neutral.primary;

            break;
    }

    const disabledTextColor = colors.fg.neutral.disabled;
    let disabledBgColor: string;
    let disabledBorderColor: string;

    switch (variant) {
        case 'buttonGroup':
        case 'buttonGroupActive':
        case 'buttonGroupProminent':
            disabledBgColor = colors.bg.neutral.disabled;
            disabledBorderColor = colors.border.neutral.primary;
            break;
        case 'solid':
            disabledBgColor = colors.bg.neutral.disabled;
            disabledBorderColor = 'transparent';
            break;
        case 'outline':
            disabledBgColor = colors.bg.neutral.primary;
            disabledBorderColor = colors.border.neutral.secondary;
            break;
        default:
            disabledBgColor = colors.bg.neutral.primary;
            disabledBorderColor = 'transparent';
            break;
    }

    const output: VariantColorOutput = {
        color: enabledTextColor,
        backgroundColor: enabledBgColor,
        borderColor: enabledBorderColor,
        textDecoration,
        '&:hover': {
            color: hoverTextColor,
            backgroundColor: hoverBgColor,
            borderColor: hoverBorderColor,
            textDecoration,
        },
        '&:active': {
            color: activeTextColor,
            backgroundColor: activeBgColor,
            borderColor: activeBorderColor,
            textDecoration,
        },
        '&.Mui-disabled': {
            color: disabledTextColor,
            backgroundColor: disabledBgColor,
            borderColor: disabledBorderColor,
            textDecoration,
            // Define the nested hover state for disabled
            '&:hover': {
                backgroundColor: disabledBgColor,
                borderColor: disabledBorderColor,
            },
        },
    };

    // If disabled, overwrite base, hover, and active styles
    if (disabled) {
        output.color = disabledTextColor;
        output.backgroundColor = disabledBgColor;
        output.borderColor = disabledBorderColor;
        output['&:hover'] = {
            color: disabledTextColor,
            backgroundColor: disabledBgColor,
            borderColor: disabledBorderColor,
        };
        output['&:active'] = {
            color: disabledTextColor,
            backgroundColor: disabledBgColor,
            borderColor: disabledBorderColor,
        };
        // The '&.Mui-disabled' block already contains the correct styles
    }

    return output;
}

type ButtonSizeOutput = {
    height: string;
    width: string | undefined;
    gap: number;
    paddingX: string | number;
    paddingY: string | number;
    typographyVariant: DesignSystemTypographyVariants;
    iconSize: DesignSystemIconSizing;
};

export const iconButtonSizeValues: Record<
    DesignSystemButtonSize,
    ButtonSizeOutput
> = {
    xs: {
        height: '24px',
        width: '24px',
        paddingX: 0,
        paddingY: 0,
        gap: 0.5,
        typographyVariant: 'xs',
        iconSize: 'sm',
    },
    sm: {
        height: '32px',
        width: '32px',
        paddingX: 0,
        paddingY: 0,
        gap: 0.75,
        typographyVariant: 'sm',
        iconSize: 'sm',
    },
    md: {
        height: '40px',
        width: '40px',
        paddingX: 0,
        paddingY: 0,
        gap: 1,
        typographyVariant: 'md',
        iconSize: 'md',
    },
    lg: {
        height: '48px',
        width: '48px',
        paddingX: 0,
        paddingY: 0,
        gap: 1,
        typographyVariant: 'lg',
        iconSize: 'md',
    },
};

export const linkButtonSizeValues: Record<
    DesignSystemButtonSize,
    ButtonSizeOutput
> = {
    xs: {
        height: '16px',
        width: 'fit-content',
        paddingX: 0,
        paddingY: 0,
        gap: 0.5,
        typographyVariant: 'xs',
        iconSize: 'sm',
    },
    sm: {
        height: '20px',
        width: 'fit-content',
        paddingX: 0,
        paddingY: 0,
        gap: 0.75,
        typographyVariant: 'sm',
        iconSize: 'sm',
    },
    md: {
        height: '24px',
        width: 'fit-content',
        paddingX: 0,
        paddingY: 0,
        gap: 1,
        typographyVariant: 'md',
        iconSize: 'md',
    },
    lg: {
        height: '28px',
        width: 'fit-content',
        paddingX: 0,
        paddingY: 0,
        gap: 1,
        typographyVariant: 'lg',
        iconSize: 'md',
    },
};

export const buttonSizeValues: Record<
    DesignSystemButtonSize,
    ButtonSizeOutput
> = {
    xs: {
        height: '24px',
        width: undefined,
        paddingX: 1,
        paddingY: 0.5,
        gap: 0.5,
        typographyVariant: 'xs',
        iconSize: 'sm',
    },
    sm: {
        height: '32px',
        width: undefined,
        paddingX: 1.25,
        paddingY: 0.75,
        gap: 0.75,
        typographyVariant: 'sm',
        iconSize: 'sm',
    },
    md: {
        height: '40px',
        width: undefined,
        paddingX: 2,
        paddingY: 1,
        gap: 1,
        typographyVariant: 'md',
        iconSize: 'md',
    },
    lg: {
        height: '48px',
        width: undefined,
        paddingX: 2.5,
        paddingY: 1.25,
        gap: 1,
        typographyVariant: 'lg',
        iconSize: 'md',
    },
};

type SizeInput = {
    size: DesignSystemButtonSize;
    variant: DesignSystemButtonVariant;
    iconOnly?: boolean;
};

type SizeOutput = {
    height: number | string;
    minWidth: number | string | undefined;
    gap: number;
    width: number | string;
    paddingX: number | string;
    paddingY: number | string;
};

export function getSizeStyles({
    size,
    variant,
    iconOnly,
}: SizeInput): SizeOutput {
    const sizeDef = iconOnly
        ? iconButtonSizeValues[size]
        : variant === 'link'
        ? linkButtonSizeValues[size]
        : buttonSizeValues[size];

    return {
        height: sizeDef.height,
        minWidth: sizeDef.width,
        width: sizeDef.width ?? 'auto',
        gap: sizeDef.gap,
        paddingX: sizeDef.paddingX,
        paddingY: sizeDef.paddingY,
    };
}

export const buttonRadiusValues: Record<
    DesignSystemButtonSize,
    Record<DesignSystemButtonRadius, DesignSystemBorderRadius>
> = {
    xs: {
        full: 'full',
        rounded: 'sm',
        none: 'none',
    },
    sm: {
        full: 'full',
        rounded: 'md',
        none: 'none',
    },
    md: {
        full: 'full',
        rounded: 'md',
        none: 'none',
    },
    lg: {
        full: 'full',
        rounded: 'md',
        none: 'none',
    },
};

export const loadingIndicatorHorizontalPositionValues: Record<
    DesignSystemButtonSize,
    number
> = {
    xs: 7,
    sm: 8,
    md: 12,
    lg: 16,
};
